/**
 * Employer Geographic Risk Map Component
 * Interactive map showing employee risk distribution, hotspots, and SDOH factors
 * Uses employer-specific endpoints for geographic risk data
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  MapPin,
  AlertTriangle,
  Users,
  Layers,
  Download,
  RefreshCw,
  Filter,
  X,
  Search,
  Car,
  Home,
  DollarSign,
  Baby,
  Stethoscope,
  ZoomIn,
  ZoomOut,
  Locate,
  Eye,
  EyeOff,
  User,
  Building,
  TrendingUp,
  Navigation,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';

interface EmployeeRisk {
  employee_id: number;
  name: string;
  department?: string;
  job_title?: string;
  latitude: number;
  longitude: number;
  zip_code: string;
  city?: string;
  churn_probability: number;
  sdoh_score: number;
  risk_level: 'low' | 'moderate' | 'elevated' | 'high' | 'critical';
  risk_factors: string[];
}

interface LocationAggregate {
  zip_code: string;
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  employee_count: number;
  high_risk_count: number;
  avg_churn_risk: number;
  avg_sdoh_score: number;
  risk_breakdown: {
    housing: number;
    transportation: number;
    childcare: number;
    financial: number;
    medical_access: number;
  };
  area_data?: {
    median_rent?: number;
    rent_burden_pct?: number;
    walk_score?: number;
    transit_score?: number;
    is_childcare_desert?: boolean;
    area_risk_index?: number;
  };
}

interface Hotspot {
  id: string;
  center_lat: number;
  center_lon: number;
  radius_miles: number;
  employee_count: number;
  high_risk_count: number;
  avg_risk_score: number;
  primary_risk_factors: string[];
  zip_code?: string;
  city?: string;
  severity: 'elevated' | 'high' | 'critical';
  employee_ids?: number[];
}

interface GeographicRiskData {
  employer_id: number;
  locations: LocationAggregate[];
  employees: EmployeeRisk[];
  hotspots: Hotspot[];
  summary: {
    total_locations: number;
    total_employees_mapped: number;
    total_hotspots: number;
    high_risk_employees: number;
    critical_hotspots: number;
  };
}

interface EmployerGeographicRiskMapProps {
  employerId: number;
  onEmployeeClick?: (employeeId: number) => void;
}

const getRiskColor = (riskLevel: string): string => {
  switch (riskLevel) {
    case 'critical': return '#DC2626'; // Red
    case 'high': return '#F97316'; // Orange
    case 'elevated': return '#EAB308'; // Yellow
    case 'moderate': return '#3B82F6'; // Blue
    case 'low': return '#22C55E'; // Green
    default: return '#6B7280'; // Gray
  }
};

const getSeverityColor = (severity: string): string => {
  switch (severity) {
    case 'critical': return 'rgba(220, 38, 38, 0.3)';
    case 'high': return 'rgba(249, 115, 22, 0.3)';
    case 'elevated': return 'rgba(234, 179, 8, 0.3)';
    default: return 'rgba(107, 114, 128, 0.3)';
  }
};

const getRiskIcon = (factor: string) => {
  switch (factor) {
    case 'housing': return Home;
    case 'transportation': return Car;
    case 'childcare': return Baby;
    case 'financial': return DollarSign;
    case 'medical_access': return Stethoscope;
    default: return AlertTriangle;
  }
};

export const EmployerGeographicRiskMap: React.FC<EmployerGeographicRiskMapProps> = ({
  employerId,
  onEmployeeClick,
}) => {
  const { t } = useAppTranslation();
  const { token: contextToken } = useAuth();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const hotspotsRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);

  const [data, setData] = useState<GeographicRiskData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // View options
  const [showEmployees, setShowEmployees] = useState(false);
  const [showHotspots, setShowHotspots] = useState(true);
  const [showLocations, setShowLocations] = useState(true);
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<LocationAggregate | null>(null);
  const [selectedHotspot, setSelectedHotspot] = useState<Hotspot | null>(null);

  // Search and location
  const [zipSearch, setZipSearch] = useState<string>('');
  const [locatingUser, setLocatingUser] = useState(false);
  const userLocationMarkerRef = useRef<any>(null);

  const getAuthToken = useCallback((): string | null => {
    if (typeof window === 'undefined') return null;
    // Check both auth_token (admin/CBO portal) and member_token (member/employer portal)
    return contextToken || localStorage.getItem('auth_token') || localStorage.getItem('member_token');
  }, [contextToken]);

  const fetchGeographicRisk = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch with include_employees to get individual markers for hotspot detection
      const response = await authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/geographic-risk?include_employees=true`);

      if (!response.ok) {
        throw new Error(`Failed to fetch geographic risk data: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('[EmployerGeographicRiskMap] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load geographic data');
    } finally {
      setLoading(false);
    }
  }, [employerId, getAuthToken]);

  // Load Google Maps script
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => setMapLoaded(true);
      document.head.appendChild(script);
    } else if (window.google) {
      setMapLoaded(true);
    }
  }, []);

  // Fetch data on mount
  useEffect(() => {
    const authToken = getAuthToken();
    if (employerId && authToken) {
      fetchGeographicRisk();
    }
  }, [employerId, fetchGeographicRisk]);

  // Initialize map when ready
  useEffect(() => {
    if (mapLoaded && !mapInstanceRef.current && mapRef.current && !loading && data) {
      initializeMap();
    }
  }, [mapLoaded, data, loading]);

  // Update markers when filters change
  useEffect(() => {
    if (mapInstanceRef.current && data) {
      updateMarkers();
    }
  }, [showEmployees, showHotspots, showLocations, riskFilter, data]);

  const initializeMap = () => {
    if (!mapRef.current || !window.google || !data) return;

    // Calculate center from locations
    let centerLat = 37.7749;
    let centerLng = -122.4194;

    if (data.locations.length > 0) {
      const avgLat = data.locations.reduce((sum, loc) => sum + loc.latitude, 0) / data.locations.length;
      const avgLng = data.locations.reduce((sum, loc) => sum + loc.longitude, 0) / data.locations.length;
      centerLat = avgLat;
      centerLng = avgLng;
    }

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: centerLat, lng: centerLng },
      zoom: 10,
      styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      ],
    });

    mapInstanceRef.current = map;
    infoWindowRef.current = new window.google.maps.InfoWindow();

    updateMarkers();
  };

  const updateMarkers = () => {
    if (!mapInstanceRef.current || !data) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    hotspotsRef.current.forEach(circle => circle.setMap(null));
    hotspotsRef.current = [];

    // Add location markers (aggregated by ZIP)
    if (showLocations) {
      data.locations.forEach(location => {
        const riskScore = location.avg_sdoh_score;
        const riskLevel = riskScore >= 75 ? 'critical' : riskScore >= 60 ? 'high' : riskScore >= 45 ? 'elevated' : riskScore >= 30 ? 'moderate' : 'low';

        if (riskFilter !== 'all' && riskLevel !== riskFilter) return;

        const marker = new window.google.maps.Marker({
          position: { lat: location.latitude, lng: location.longitude },
          map: mapInstanceRef.current,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8 + Math.min(location.employee_count, 20),
            fillColor: getRiskColor(riskLevel),
            fillOpacity: 0.8,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
          title: `${location.city || location.zip_code}: ${location.employee_count} employees`,
        });

        marker.addListener('click', () => {
          setSelectedLocation(location);
          setSelectedHotspot(null);

          const content = `
            <div style="padding: 12px; max-width: 280px;">
              <h3 style="margin: 0 0 8px 0; font-weight: bold;">${location.city || 'Unknown'}, ${location.state || ''}</h3>
              <p style="margin: 0 0 4px 0; color: #666;">ZIP: ${location.zip_code}</p>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 8px;">
                <div>
                  <span style="color: #666; font-size: 12px;">Employees</span>
                  <p style="margin: 0; font-weight: bold;">${location.employee_count}</p>
                </div>
                <div>
                  <span style="color: #666; font-size: 12px;">High Risk</span>
                  <p style="margin: 0; font-weight: bold; color: ${location.high_risk_count > 0 ? '#DC2626' : '#22C55E'};">${location.high_risk_count}</p>
                </div>
                <div>
                  <span style="color: #666; font-size: 12px;">Avg SDOH Score</span>
                  <p style="margin: 0; font-weight: bold;">${location.avg_sdoh_score.toFixed(1)}</p>
                </div>
                <div>
                  <span style="color: #666; font-size: 12px;">Churn Risk</span>
                  <p style="margin: 0; font-weight: bold;">${(location.avg_churn_risk * 100).toFixed(1)}%</p>
                </div>
              </div>
            </div>
          `;

          infoWindowRef.current?.setContent(content);
          infoWindowRef.current?.open(mapInstanceRef.current, marker);
        });

        markersRef.current.push(marker);
      });
    }

    // Add individual employee markers
    if (showEmployees && data.employees) {
      data.employees.forEach(employee => {
        if (riskFilter !== 'all' && employee.risk_level !== riskFilter) return;

        const marker = new window.google.maps.Marker({
          position: { lat: employee.latitude, lng: employee.longitude },
          map: mapInstanceRef.current,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 6,
            fillColor: getRiskColor(employee.risk_level),
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeWeight: 1,
          },
          title: employee.name,
        });

        marker.addListener('click', () => {
          const content = `
            <div style="padding: 12px; max-width: 250px;">
              <h3 style="margin: 0 0 4px 0; font-weight: bold;">${employee.name}</h3>
              <p style="margin: 0 0 8px 0; color: #666; font-size: 12px;">${employee.job_title || ''} ${employee.department ? `• ${employee.department}` : ''}</p>
              <div style="display: flex; gap: 8px; margin-bottom: 8px;">
                <span style="background: ${getRiskColor(employee.risk_level)}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px;">
                  ${employee.risk_level.toUpperCase()}
                </span>
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                <div>
                  <span style="color: #666; font-size: 11px;">SDOH Score</span>
                  <p style="margin: 0; font-weight: bold;">${employee.sdoh_score.toFixed(1)}</p>
                </div>
                <div>
                  <span style="color: #666; font-size: 11px;">Churn Risk</span>
                  <p style="margin: 0; font-weight: bold;">${(employee.churn_probability * 100).toFixed(1)}%</p>
                </div>
              </div>
              ${employee.risk_factors.length > 0 ? `
                <div style="margin-top: 8px;">
                  <span style="color: #666; font-size: 11px;">Risk Factors:</span>
                  <p style="margin: 4px 0 0 0; font-size: 12px;">${employee.risk_factors.join(', ')}</p>
                </div>
              ` : ''}
              ${onEmployeeClick ? `
                <button
                  onclick="window.employerMapClickEmployee(${employee.employee_id})"
                  style="margin-top: 8px; width: 100%; padding: 6px; background: #3B82F6; color: white; border: none; border-radius: 4px; cursor: pointer;"
                >
                  View Details
                </button>
              ` : ''}
            </div>
          `;

          infoWindowRef.current?.setContent(content);
          infoWindowRef.current?.open(mapInstanceRef.current, marker);
        });

        markersRef.current.push(marker);
      });
    }

    // Add hotspot circles
    if (showHotspots && data.hotspots) {
      data.hotspots.forEach(hotspot => {
        const circle = new window.google.maps.Circle({
          map: mapInstanceRef.current,
          center: { lat: hotspot.center_lat, lng: hotspot.center_lon },
          radius: hotspot.radius_miles * 1609.34, // Convert miles to meters
          fillColor: getSeverityColor(hotspot.severity),
          fillOpacity: 0.4,
          strokeColor: getRiskColor(hotspot.severity),
          strokeOpacity: 0.8,
          strokeWeight: 2,
          clickable: true,
        });

        circle.addListener('click', () => {
          setSelectedHotspot(hotspot);
          setSelectedLocation(null);

          const content = `
            <div style="padding: 12px; max-width: 280px;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <span style="background: ${getRiskColor(hotspot.severity)}; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: bold;">
                  ${hotspot.severity.toUpperCase()} HOTSPOT
                </span>
              </div>
              <p style="margin: 0 0 8px 0; color: #666;">${hotspot.city || ''} ${hotspot.zip_code ? `(${hotspot.zip_code})` : ''}</p>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                <div>
                  <span style="color: #666; font-size: 11px;">Employees</span>
                  <p style="margin: 0; font-weight: bold;">${hotspot.employee_count}</p>
                </div>
                <div>
                  <span style="color: #666; font-size: 11px;">High Risk</span>
                  <p style="margin: 0; font-weight: bold; color: #DC2626;">${hotspot.high_risk_count}</p>
                </div>
                <div>
                  <span style="color: #666; font-size: 11px;">Avg Risk Score</span>
                  <p style="margin: 0; font-weight: bold;">${hotspot.avg_risk_score.toFixed(1)}</p>
                </div>
                <div>
                  <span style="color: #666; font-size: 11px;">Radius</span>
                  <p style="margin: 0; font-weight: bold;">${hotspot.radius_miles} mi</p>
                </div>
              </div>
              ${hotspot.primary_risk_factors.length > 0 ? `
                <div style="margin-top: 8px;">
                  <span style="color: #666; font-size: 11px;">Primary Risk Factors:</span>
                  <p style="margin: 4px 0 0 0; font-size: 12px;">${hotspot.primary_risk_factors.join(', ')}</p>
                </div>
              ` : ''}
            </div>
          `;

          infoWindowRef.current?.setContent(content);
          infoWindowRef.current?.setPosition({ lat: hotspot.center_lat, lng: hotspot.center_lon });
          infoWindowRef.current?.open(mapInstanceRef.current);
        });

        hotspotsRef.current.push(circle);
      });
    }
  };

  // Expose click handler for InfoWindow buttons
  useEffect(() => {
    if (typeof window !== 'undefined' && onEmployeeClick) {
      (window as any).employerMapClickEmployee = onEmployeeClick;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).employerMapClickEmployee;
      }
    };
  }, [onEmployeeClick]);

  const handleZoomIn = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(mapInstanceRef.current.getZoom() - 1);
    }
  };

  const handleResetView = () => {
    if (mapInstanceRef.current && data && data.locations.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      data.locations.forEach(loc => {
        bounds.extend({ lat: loc.latitude, lng: loc.longitude });
      });
      mapInstanceRef.current.fitBounds(bounds);
    }
  };

  // Handle ZIP code search
  const handleZipSearch = useCallback(() => {
    if (!zipSearch.trim() || !mapInstanceRef.current || !window.google) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: zipSearch.trim() + ', USA' }, (results: any, status: any) => {
      if (status === 'OK' && results && results.length > 0) {
        const location = results[0].geometry.location;
        mapInstanceRef.current.setCenter(location);
        mapInstanceRef.current.setZoom(13);

        // Add a temporary marker for the searched location
        if (userLocationMarkerRef.current) {
          userLocationMarkerRef.current.setMap(null);
        }

        userLocationMarkerRef.current = new window.google.maps.Marker({
          position: location,
          map: mapInstanceRef.current,
          title: `Search: ${zipSearch}`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: '#3B82F6',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 3,
          },
          zIndex: 1000,
        });

        // Open info window
        infoWindowRef.current?.setContent(`
          <div style="padding: 8px;">
            <p style="margin: 0; font-weight: bold;">Searched Location</p>
            <p style="margin: 4px 0 0 0; color: #666;">${results[0].formatted_address}</p>
          </div>
        `);
        infoWindowRef.current?.open(mapInstanceRef.current, userLocationMarkerRef.current);
      } else {
        console.error('Geocode failed:', status);
        alert('Could not find location for: ' + zipSearch);
      }
    });
  }, [zipSearch]);

  // Handle current location
  const handleCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setLocatingUser(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        if (mapInstanceRef.current && window.google) {
          const userLocation = { lat: latitude, lng: longitude };
          mapInstanceRef.current.setCenter(userLocation);
          mapInstanceRef.current.setZoom(12);

          // Add or update user location marker
          if (userLocationMarkerRef.current) {
            userLocationMarkerRef.current.setMap(null);
          }

          userLocationMarkerRef.current = new window.google.maps.Marker({
            position: userLocation,
            map: mapInstanceRef.current,
            title: 'Your Location',
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: '#3B82F6',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 3,
            },
            zIndex: 1000,
          });

          // Add a pulsing effect circle
          new window.google.maps.Circle({
            map: mapInstanceRef.current,
            center: userLocation,
            radius: 200,
            fillColor: '#3B82F6',
            fillOpacity: 0.2,
            strokeColor: '#3B82F6',
            strokeOpacity: 0.5,
            strokeWeight: 1,
          });

          infoWindowRef.current?.setContent(`
            <div style="padding: 8px;">
              <p style="margin: 0; font-weight: bold;">Your Current Location</p>
              <p style="margin: 4px 0 0 0; color: #666; font-size: 12px;">
                ${latitude.toFixed(4)}, ${longitude.toFixed(4)}
              </p>
            </div>
          `);
          infoWindowRef.current?.open(mapInstanceRef.current, userLocationMarkerRef.current);
        }

        setLocatingUser(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        let message = 'Unable to get your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = 'Location access denied. Please enable location permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            message = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            message = 'Location request timed out.';
            break;
        }
        alert(message);
        setLocatingUser(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
            <p className="text-muted-foreground">{t('employer.analytics.loadingGeographicRisk')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-500" />
            <p className="text-red-500 font-medium">{t('employer.analytics.errorLoadingMap')}</p>
            <p className="text-muted-foreground text-sm mt-1">{error}</p>
            <Button onClick={fetchGeographicRisk} className="mt-4" variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('employer.common.retry')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {t('employer.analytics.geographicRiskMapTitle')}
            </CardTitle>
            <CardDescription>
              {t('employer.analytics.geographicRiskMapDesc')}
            </CardDescription>
          </div>
          <Button onClick={fetchGeographicRisk} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-1" />
            {t('employer.common.refresh')}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Summary Stats */}
        {data?.summary && (
          <div className="grid grid-cols-5 gap-2 px-4 py-2 bg-muted/50 border-y text-sm">
            <div className="text-center">
              <p className="text-muted-foreground text-xs">{t('employer.analytics.locations')}</p>
              <p className="font-bold">{data.summary.total_locations}</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground text-xs">{t('employer.analytics.employees')}</p>
              <p className="font-bold">{data.summary.total_employees_mapped}</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground text-xs">{t('employer.analytics.highRisk')}</p>
              <p className="font-bold text-orange-600">{data.summary.high_risk_employees}</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground text-xs">{t('employer.analytics.hotspots')}</p>
              <p className="font-bold text-red-600">{data.summary.total_hotspots}</p>
            </div>
            <div className="text-center">
              <p className="text-muted-foreground text-xs">{t('employer.analytics.critical')}</p>
              <p className="font-bold text-red-700">{data.summary.critical_hotspots}</p>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/30">
          <div className="flex items-center gap-2 flex-1">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('employer.analytics.searchPlaceholder')}
              value={zipSearch}
              onChange={(e) => setZipSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleZipSearch()}
              className="h-8 max-w-xs"
            />
            <Button variant="default" size="sm" onClick={handleZipSearch} className="h-8">
              {t('employer.common.search')}
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCurrentLocation}
            disabled={locatingUser}
            className="h-8"
          >
            {locatingUser ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Navigation className="h-4 w-4 mr-1" />
            )}
            {t('employer.analytics.myLocation')}
          </Button>
        </div>

        {/* Map Controls */}
        <div className="flex items-center gap-4 px-4 py-2 border-b flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">{t('employer.analytics.view')}:</Label>
            <div className="flex items-center gap-1">
              <Button
                variant={showLocations ? "default" : "outline"}
                size="sm"
                onClick={() => setShowLocations(!showLocations)}
                className="h-7 px-2"
              >
                <Building className="h-3 w-3 mr-1" />
                {t('employer.analytics.locations')}
              </Button>
              <Button
                variant={showEmployees ? "default" : "outline"}
                size="sm"
                onClick={() => setShowEmployees(!showEmployees)}
                className="h-7 px-2"
              >
                <User className="h-3 w-3 mr-1" />
                {t('employer.analytics.employees')}
              </Button>
              <Button
                variant={showHotspots ? "default" : "outline"}
                size="sm"
                onClick={() => setShowHotspots(!showHotspots)}
                className="h-7 px-2"
              >
                <AlertTriangle className="h-3 w-3 mr-1" />
                {t('employer.analytics.hotspots')}
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm">{t('employer.analytics.risk')}:</Label>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="h-7 w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('employer.common.all')}</SelectItem>
                <SelectItem value="critical">{t('employer.retention.critical')}</SelectItem>
                <SelectItem value="high">{t('employer.retention.high')}</SelectItem>
                <SelectItem value="elevated">{t('employer.analytics.elevated')}</SelectItem>
                <SelectItem value="moderate">{t('employer.retention.moderate')}</SelectItem>
                <SelectItem value="low">{t('employer.retention.low')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1 ml-auto">
            <Button variant="outline" size="sm" onClick={handleZoomIn} className="h-7 w-7 p-0">
              <ZoomIn className="h-3 w-3" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleZoomOut} className="h-7 w-7 p-0">
              <ZoomOut className="h-3 w-3" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleResetView} className="h-7 px-2">
              <Locate className="h-3 w-3 mr-1" />
              {t('employer.analytics.reset')}
            </Button>
          </div>
        </div>

        {/* Map Container */}
        <div ref={mapRef} className="h-[500px] w-full" />

        {/* Legend */}
        <div className="px-4 py-2 border-t bg-muted/30">
          <div className="flex items-center gap-4 flex-wrap text-xs">
            <span className="font-medium">{t('employer.analytics.riskLevels')}:</span>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ background: getRiskColor('critical') }} />
              <span>{t('employer.retention.critical')}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ background: getRiskColor('high') }} />
              <span>{t('employer.retention.high')}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ background: getRiskColor('elevated') }} />
              <span>{t('employer.analytics.elevated')}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ background: getRiskColor('moderate') }} />
              <span>{t('employer.retention.moderate')}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ background: getRiskColor('low') }} />
              <span>{t('employer.retention.low')}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmployerGeographicRiskMap;
